import React, { FC, useCallback, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { toast } from 'react-toastify';
import { Alert, Collapse } from 'reactstrap';
import { INPUT_TYPES } from 'views/Widgets/Inputs/ValidatedInputTypes';
import ModalEditor from 'views/Widgets/Modals/ModalEditor';

export interface WorkflowItem {
  id: number | null;
  workflow_type: string;
  workflow_type_display: string;
  description: string;
  prompt_summary: string;
  instructions: string;
  created_at: string | null;
  updated_at: string | null;
}

interface Props {
  isOpen: boolean;
  workflow: WorkflowItem;
  toggle: () => void;
  onSuccess: (updated: WorkflowItem) => void;
}

const EditWorkflowModal: FC<Props> = ({
  isOpen,
  workflow,
  toggle,
  onSuccess,
}) => {
  const { formatMessage } = useIntl();

  const handleCallback = useCallback(
    (data: unknown, error: unknown) => {
      if (data) {
        toast.success(
          formatMessage({
            id: 'app.views.ai.agents.workflows_admin.edit_modal.success',
            defaultMessage: 'Instructions saved successfully.',
          })
        );
        onSuccess({
          ...workflow,
          ...(data as WorkflowItem),
        });
      } else if (error) {
        toast.error(
          formatMessage({
            id: 'app.views.ai.agents.workflows_admin.edit_modal.error',
            defaultMessage: 'Failed to save workflow instructions.',
          })
        );
      }
    },
    [formatMessage, onSuccess, workflow]
  );

  const [isBaseOpen, setIsBaseOpen] = useState(false);

  const renderInputs = useCallback(
    ([instructions]: React.ReactNode[]) => (
      <>
        <Alert color="info">{workflow.description}</Alert>
        <Alert color="light" className="mb-3">
          <div
            className="d-flex align-items-center justify-content-between"
            style={{ cursor: 'pointer' }}
            onClick={() => setIsBaseOpen((prev) => !prev)}
          >
            <strong>
              <FormattedMessage
                id="app.views.ai.agents.workflows_admin.edit_modal.base_instructions_label"
                defaultMessage="Base instructions"
              />
            </strong>
            <i className={`fe fe-chevron-${isBaseOpen ? 'up' : 'down'}`} />
          </div>
          <Collapse isOpen={isBaseOpen}>
            <p className="mb-0 mt-2" style={{ whiteSpace: 'pre-wrap' }}>
              {workflow.prompt_summary}
            </p>
          </Collapse>
        </Alert>
        {instructions}
      </>
    ),
    [workflow.description, workflow.prompt_summary, isBaseOpen]
  );

  const inputs = useMemo(
    () => [
      {
        name: 'instructions',
        label: formatMessage({
          id: 'app.views.ai.agents.workflows_admin.edit_modal.instructions_label',
          defaultMessage: 'Custom instructions',
        }),
        helperText: formatMessage({
          id: 'app.views.ai.agents.workflows_admin.edit_modal.instructions_help',
          defaultMessage:
            'Custom instructions enhance the base instructions above. When a custom instruction conflicts with a base instruction, the custom instruction takes priority. Non-conflicting instructions from both are followed. Leave empty to use only the base instructions.',
        }),
        placeholder: formatMessage({
          id: 'app.views.ai.agents.workflows_admin.edit_modal.instructions_placeholder',
          defaultMessage: 'Enter custom instructions for this workflow...',
        }),
        type: INPUT_TYPES.TEXTAREA,
        required: false,
        minRows: 8,
      },
    ],
    [formatMessage]
  );

  return (
    <ModalEditor
      isOpen={isOpen}
      toggle={toggle}
      object={{ instructions: workflow.instructions }}
      inputs={inputs}
      method="PATCH"
      url={`ai-workflows/${workflow.workflow_type}`}
      submitText={formatMessage({
        id: 'app.views.ai.agents.workflows_admin.edit_modal.save',
        defaultMessage: 'Save',
      })}
      title={formatMessage(
        {
          id: 'app.views.ai.agents.workflows_admin.edit_modal.title',
          defaultMessage: 'Edit {workflowName} instructions',
        },
        { workflowName: workflow.workflow_type_display }
      )}
      callback={handleCallback}
      renderInputs={renderInputs}
      keepOpenOnSuccess={true}
    />
  );
};

export default React.memo(EditWorkflowModal);
